/**
 * Het tabblad Illustraties: per tekst de drie stijlen (lijntekening, foto,
 * schattig). Eerst een lijst van teksten; kies er een, en je ziet de drie
 * kaarten (onderdelen/IllustratieKaart.js), met een knop om de ontbrekende
 * stijlen in één keer te laten maken.
 *
 * De plaatjes staan achter het token en komen daarom als data-URL binnen;
 * die worden hier per illustratie bewaard, zodat ze niet steeds opnieuw
 * worden opgehaald.
 */
import React, { useEffect, useState } from 'react';
import { Check, Image, RefreshCw, Sparkles } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { IllustratieKaart, STIJLEN } from '../onderdelen/IllustratieKaart';

export function Illustraties({ actief, meldFout }) {
  const [teksten, setTeksten] = useState([]);
  const [laden, setLaden] = useState(false);
  const [gekozenId, setGekozenId] = useState(null);
  const [bezig, setBezig] = useState({});            // per tekst en stijl: wordt hij gemaakt?
  const [allesBezig, setAllesBezig] = useState(false);
  const [bericht, setBericht] = useState('');
  const [beelden, setBeelden] = useState({});        // per illustratie: de data-URL

  const gekozen = teksten.find(t => t.id === gekozenId) || null;

  const haalBeeld = async (id) => {
    try {
      const data = await api.haalIllustratieBeeld(id);
      setBeelden(prev => ({ ...prev, [id]: data.data_url }));
    } catch (err) {
      console.error('Error fetching illustration image:', err);
    }
  };

  const haalBeelden = (illustraties) => {
    illustraties.forEach(ill => {
      if (ill.has_image && !beelden[ill.id]) haalBeeld(ill.id);
    });
  };

  const herlaad = async () => {
    const data = await api.haalIllustraties();
    setTeksten(data);
    return data;
  };

  // Elke keer dat het tabblad opengaat: de lijst, en alvast alle plaatjes.
  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    herlaad()
      .then(data => haalBeelden(data.flatMap(t => t.illustrations)))
      .catch(err => meldFout(err.message))
      .finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  /** Eén stijl maken (of opnieuw maken); true als het lukte. */
  const maak = async (stijl, aanwijzing) => {
    const sleutel = `${gekozen.id}-${stijl}`;
    setBezig(prev => ({ ...prev, [sleutel]: true }));
    setBericht('');
    try {
      const uitkomst = await api.maakIllustratie(gekozen.id, stijl, aanwijzing);
      if (!uitkomst.success) {
        setBericht(uitkomst.message || 'Fout bij genereren illustratie');
        return false;
      }
      setBericht(`${uitkomst.message}`);
      if (uitkomst.illustration_id) haalBeeld(uitkomst.illustration_id);
      const data = await herlaad();
      const bijgewerkt = data.find(t => t.id === gekozen.id);
      if (bijgewerkt) haalBeelden(bijgewerkt.illustrations);
      return true;
    } catch (err) {
      setBericht('Fout: ' + err.message);
      return false;
    } finally {
      setBezig(prev => ({ ...prev, [sleutel]: false }));
    }
  };

  const maakAlles = async () => {
    setAllesBezig(true);
    setBericht('Alle drie illustraties worden gegenereerd...');
    try {
      const uitkomst = await api.maakAlleIllustraties(gekozen.id);
      if (!uitkomst.success) {
        setBericht(uitkomst.message || 'Fout bij genereren illustraties');
        return;
      }
      setBericht('Alle illustraties zijn verwerkt!');
      const data = await herlaad();
      const bijgewerkt = data.find(t => t.id === gekozen.id);
      if (bijgewerkt) haalBeelden(bijgewerkt.illustrations);
    } catch (err) {
      setBericht('Fout: ' + err.message);
    } finally {
      setAllesBezig(false);
    }
  };

  const verwijder = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze illustratie wilt verwijderen?')) return;
    try {
      await api.verwijderIllustratie(id);
      await herlaad();
    } catch (err) {
      meldFout(err.message);
    }
  };

  const kies = (tekst) => {
    setGekozenId(tekst.id);
    haalBeelden(tekst.illustrations);
  };

  const sluit = () => {
    setGekozenId(null);
    setBericht('');
  };

  if (laden) return <p>Laden...</p>;

  return (
    <div className="card">
      <h2 className="mb-16">Illustraties</h2>
      <p className="text-muted mb-16">
        Selecteer een tekst om illustraties te genereren in drie stijlen:
        Lijntekening, Foto en Schattig.
      </p>

      {bericht && (
        <div className={`alert ${bericht.includes('Fout') ? 'alert-error' : 'alert-info'} mb-16`}>{bericht}</div>
      )}

      {!gekozen ? (
        teksten.length === 0 ? (
          <p className="text-muted">Geen teksten gevonden. Maak eerst teksten aan in de Teksten tab.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Tekst</th>
                <th>Paragrafen</th>
                <th>Illustraties</th>
                <th>Actie</th>
              </tr>
            </thead>
            <tbody>
              {teksten.map(tekst => (
                <tr key={tekst.id}>
                  <td>
                    <strong>{tekst.titel}</strong>
                    {tekst.beschrijving && <p className="text-muted text-small">{tekst.beschrijving}</p>}
                  </td>
                  <td>{tekst.paragraph_count || 0}</td>
                  <td>
                    <div className="flex gap-8">
                      {tekst.illustrations.find(i => i.style === 'lijntekening') && (
                        <span className="badge" style={{ background: 'var(--inkt)', color: 'var(--wit)' }}>Lijn</span>
                      )}
                      {tekst.illustrations.find(i => i.style === 'foto') && (
                        <span className="badge" style={{ background: 'var(--blauw)', color: 'var(--wit)' }}>Foto</span>
                      )}
                      {tekst.illustrations.find(i => i.style === 'schattig') && (
                        <span className="badge" style={{ background: 'var(--fout-2)', color: 'var(--wit)' }}>Schattig</span>
                      )}
                      {tekst.illustrations.length === 0 && <span className="text-muted">Geen</span>}
                    </div>
                  </td>
                  <td>
                    <button className="btn btn-primary" style={{ padding: '8px 16px' }} onClick={() => kies(tekst)}>
                      <Image size={14} /> Illustraties
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : (
        <div className="card" style={{ background: 'var(--blauw-vlak-2)' }}>
          <div className="flex-between mb-16">
            <div>
              <h3>{gekozen.titel}</h3>
              {gekozen.beschrijving && <p className="text-muted">{gekozen.beschrijving}</p>}
            </div>
            <button className="btn btn-success" onClick={sluit} style={{ padding: '12px 24px' }}>
              <Check size={16} /> Opslaan en sluiten
            </button>
          </div>

          {gekozen.illustrations.length < 3 && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <button className="btn btn-primary" onClick={maakAlles} disabled={allesBezig} style={{ padding: '16px 32px', fontSize: 16 }}>
                {allesBezig ? <><RefreshCw size={18} className="spinning" /> Illustraties aan het maken...</> : <><Sparkles size={18} /> Maak illustraties</>}
              </button>
            </div>
          )}

          <div className="grid-3" style={{ gap: '24px' }}>
            {STIJLEN.map(soort => {
              const illustratie = gekozen.illustrations.find(i => i.style === soort.stijl);
              return (
                <IllustratieKaart
                  key={soort.stijl}
                  soort={soort}
                  illustratie={illustratie}
                  beeld={illustratie ? beelden[illustratie.id] : null}
                  bezig={!!bezig[`${gekozen.id}-${soort.stijl}`]}
                  onMaak={(aanwijzing) => maak(soort.stijl, aanwijzing)}
                  onVerwijder={() => verwijder(illustratie.id)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
